import { useState, useMemo, useCallback } from "react";
import { z } from "zod";
import { checkMessage } from "@/lib/zodMessages";
import { useLingui } from "@lingui/react/macro";
import { Trans } from "@lingui/react";
import { BaseEditableTable } from "@/components/table/BaseEditableTable";
import type { EditableCellConfig } from "@/components/table/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Plus, Trash2 } from "lucide-react";
import {
  useCreateProgressEntry,
  useUpdateProgressEntry,
  useDeleteProgressEntry,
} from "@/hooks/useSettlingIn";
import { useSettlingInIndicators } from "@/hooks/useSettlingInSettings";
import { formatEmployeeName, useEmployeePickerNames, useEmployeePickerSearch } from "@/hooks/useEmployees";
import type { ProgressEntryResponse, IndicatorDefinitionDTO, ProgressIndicatorDTO, ScheduleEntryResponse } from "@/types/settlingIn";
import { format } from "date-fns";
import { toOrgDateOnly } from "@/utils/dateUtils";
import { cn } from "@/lib/utils";
import { getIndicatorStyle } from "@/lib/settlingInIndicator";
import { EmployeeSearchSelect } from "@/components/settling-in/EmployeeSearchSelect";
import { ProgressTrendMatrix } from "@/components/settling-in/ProgressTrendMatrix";
import { BaseEditableForm } from "@/components/form/BaseEditableForm";
import type { FieldConfig, GroupConfig, EnhancedSelectOption } from "@/components/form/types";
import type { TriStateValue } from "@/components/form/fields/TriStateField";
import { typeError } from "@/lib/zodTypeError";

// Module scope, so the reference is stable and the `columns` memo below can use
// it without either listing a per-render function in its deps (which would
// rebuild every column on every render) or lying about them.

/** The Add-Progress form's shape (ST-5/S16). */
interface ProgressAddFormData extends Record<string, unknown> {
  date: string;
  employee_id?: string;
  indicators?: TriStateValue;
  comment?: string;
}

interface ProgressTabProps {
  planId: string;
  progressEntries: ProgressEntryResponse[];
  scheduleEntries?: ScheduleEntryResponse[];
}

type IndicatorValue = "good" | "neutral" | "bad" | undefined;

function TriStateButton({
  value,
  onChange,
  label,
}: {
  value: IndicatorValue;
  onChange: (value: IndicatorValue) => void;
  label: string;
}) {
  const { i18n } = useLingui();

  const cycle = useCallback(() => {
    const states: IndicatorValue[] = [undefined, "good", "neutral", "bad"];
    const currentIndex = states.indexOf(value);
    const nextIndex = (currentIndex + 1) % states.length;
    onChange(states[nextIndex]);
  }, [value, onChange]);

  // `undefined` (not yet assessed) maps to the `unset` style, which is distinct
  // from `neutral` on hue, border-style and glyph.
  const style = getIndicatorStyle(value ?? "unset");
  // Announce the LOCALIZED value, not the raw English enum the control used to
  // read out (`${label}: good`).
  const announced = `${label}: ${i18n._(style.label)}`;

  return (
    <button
      type="button"
      onClick={cycle}
      onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); cycle(); } }}
      className={cn(
        "w-10 h-10 flex items-center justify-center text-lg cursor-pointer transition-colors",
        style.chip,
        style.text,
        style.shape,
      )}
      aria-label={announced}
      title={announced}
    >
      {style.glyph}
    </button>
  );
}

export function ProgressTab({ planId, progressEntries, scheduleEntries = [] }: ProgressTabProps) {
  const { t, i18n } = useLingui();
  const [deleteEntryId, setDeleteEntryId] = useState<string | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  /**
   * Bumped on every dialog open. `BaseEditableForm` seeds RHF from
   * `defaultValues` at MOUNT ONLY, so without a changing `key` the second open
   * would still show the first entry's values.
   */
  const [addDialogNonce, setAddDialogNonce] = useState(0);

  const createMutation = useCreateProgressEntry();
  const updateMutation = useUpdateProgressEntry();
  const deleteMutation = useDeleteProgressEntry();

  const { data: indicatorsData } = useSettlingInIndicators();

  // The employee field reads the any-module picker (#1688), so a settling-in-only
  // role picks names without the EMP_LIST or detail reads.
  const searchEmployees = useEmployeePickerSearch(50, { selectableOnly: true });
  const { names: employeeNames } = useEmployeePickerNames();

  // Determine if there's a single employee across all schedule entries for pre-fill
  const defaultEmployeeId = useMemo(() => {
    const employeeIds = new Set(
      scheduleEntries
        .map((e) => e.employee_id)
        .filter((id): id is string => !!id)
    );
    return employeeIds.size === 1 ? [...employeeIds][0] : "";
  }, [scheduleEntries]);

  const activeIndicators = useMemo(
    () => (indicatorsData || []).filter((ind: IndicatorDefinitionDTO) => ind.active !== false).sort((a: IndicatorDefinitionDTO, b: IndicatorDefinitionDTO) => (a.order || 0) - (b.order || 0)),
    [indicatorsData],
  );

  const getIndicatorLabel = useCallback((indicator: IndicatorDefinitionDTO) => {
    const labels = indicator.labels || {};
    return labels[i18n.locale] || labels["en"] || labels["de"] || indicator.id;
  }, [i18n.locale]);

  const sortedEntries = useMemo(
    () => [...progressEntries].sort((a, b) => (b.date || "").localeCompare(a.date || "")),
    [progressEntries],
  );

  const getEntryIndicatorValue = useCallback((entry: ProgressEntryResponse, indicatorId: string): IndicatorValue => {
    const ind = (entry.indicators || []).find((i: ProgressIndicatorDTO) => i.indicator_id === indicatorId);
    return ind?.value as IndicatorValue;
  }, []);

  const handleToggleIndicator = useCallback(async (entry: ProgressEntryResponse, indicatorId: string, newValue: IndicatorValue) => {
    if (!entry.id) return;

    const currentIndicators = entry.indicators || [];
    const updatedIndicators = activeIndicators.map((ind: IndicatorDefinitionDTO) => {
      if (ind.id === indicatorId) {
        return { indicator_id: ind.id, value: newValue };
      }
      const existing = currentIndicators.find((i: ProgressIndicatorDTO) => i.indicator_id === ind.id);
      return { indicator_id: ind.id, value: existing?.value };
    }).filter((ind) => ind.value != null);

    await updateMutation.mutateAsync({
      planId,
      entryId: entry.id,
      data: {
        date: toOrgDateOnly(entry.date),
        employee_id: entry.employee_id,
        indicators: updatedIndicators as ProgressIndicatorDTO[],
        comment: entry.comment,
        version: entry.version ?? 0,
      },
    });
  }, [updateMutation, planId, activeIndicators]);

  const handleDelete = useCallback(async (entryId: string) => {
    await deleteMutation.mutateAsync({ planId, entryId });
  }, [deleteMutation, planId]);

  const dateRequiredMsg = t({ id: "settlingIn.progress.form.dateRequired", message: "Date is required" });

  const addSchema = useMemo(() => z.object({
    date: z.union([
      z.string().min(1, dateRequiredMsg),
      z.date({ error: typeError({ required: dateRequiredMsg }) }),
    ]).transform((val) => (val instanceof Date ? format(val, "yyyy-MM-dd") : val)),
    employee_id: z.string().optional(),
    // The tri-state record is free-form: an unset indicator is dropped at submit.
    indicators: z.record(z.string(), z.string()).optional(),
    comment: z.string().max(10000, checkMessage.maxLength(i18n)).optional(),
    // `z.record(z.string(), z.string())` widens the tri-state values to `string`, so the
    // inferred output does not structurally match ProgressAddFormData's
    // `TriStateValue`. The runtime shape is identical; only the static type is
    // widened, so one cast at the schema boundary is correct here.
  }) as unknown as z.ZodType<ProgressAddFormData, ProgressAddFormData>, [i18n, dateRequiredMsg]);

  /**
   * Name search over the whole staff (the picker, filtered in the browser), the
   * same source `EmployeeSearchSelect` uses for the table rows.
   */
  const loadEmployeeOptions = useCallback(async (query: string): Promise<EnhancedSelectOption[]> => {
    const items = await searchEmployees(query);
    return items.map((e) => ({ value: e.id, label: formatEmployeeName(e) || e.id }));
  }, [searchEmployees]);

  /**
   * Hydrate the pre-filled employee BY ID, so the trigger shows the real name
   * even when the employee is outside the first page of options. An id the
   * picker cannot resolve yields no option: the field then shows its
   * placeholder rather than a raw id.
   */
  const employeeInitialOptions = useMemo<EnhancedSelectOption[]>(() => {
    const name = defaultEmployeeId ? employeeNames.get(defaultEmployeeId) : undefined;
    return defaultEmployeeId && name ? [{ value: defaultEmployeeId, label: name }] : [];
  }, [defaultEmployeeId, employeeNames]);

  const addFields = useMemo<FieldConfig<ProgressAddFormData>[]>(() => [
    {
      name: "date",
      label: t({ id: "settlingIn.progress.form.dateLabel", message: "Date" }),
      type: "date",
      required: true,
    },
    {
      name: "employee_id",
      label: t({ id: "settlingIn.progress.form.employeeLabel", message: "Employee" }),
      type: "asyncselect",
      loadOptions: loadEmployeeOptions,
      initialOptions: employeeInitialOptions,
      placeholder: t({ id: "settlingIn.progress.selectEmployee", message: "Select employee" }),
    },
    {
      name: "indicators",
      label: t({ id: "settlingIn.progress.form.indicatorsLabel", message: "Indicators" }),
      type: "tristate",
      triStateOptions: activeIndicators.map((ind: IndicatorDefinitionDTO) => ({
        value: ind.id || "",
        label: getIndicatorLabel(ind),
      })),
      colSpan: 2,
    },
    {
      name: "comment",
      label: t({ id: "settlingIn.progress.form.commentLabel", message: "Comment" }),
      type: "textarea",
      colSpan: 2,
    },
  ], [t, loadEmployeeOptions, employeeInitialOptions, activeIndicators, getIndicatorLabel]);

  const addGroups = useMemo<GroupConfig<ProgressAddFormData>[]>(() => [
    {
      title: t({ id: "settlingIn.progress.form.groupTitle", message: "Progress Entry" }),
      fields: ["date", "employee_id", "indicators", "comment"],
    },
  ], [t]);

  const addDefaultValues = useMemo<Partial<ProgressAddFormData>>(() => ({
    date: format(new Date(), "yyyy-MM-dd"),
    // Pre-fill the employee when the plan's schedule names exactly one, matching
    // the pre-migration behaviour.
    employee_id: defaultEmployeeId || "",
    indicators: {},
    comment: "",
  }), [defaultEmployeeId]);

  const handleAdd = useCallback(async (values: ProgressAddFormData) => {
    // An UNSET indicator is omitted from the submitted array entirely — the
    // pre-migration behaviour. "Not assessed" is an absence, not a value.
    const selected = values.indicators ?? {};
    const indicators = activeIndicators
      .filter((ind: IndicatorDefinitionDTO) => {
        const v = selected[ind.id];
        return v != null && v !== "unset";
      })
      .map((ind: IndicatorDefinitionDTO) => ({
        indicator_id: ind.id,
        value: selected[ind.id],
      }));

    await createMutation.mutateAsync({
      planId,
      data: {
        date: values.date,
        employee_id: values.employee_id || undefined,
        indicators: indicators as ProgressIndicatorDTO[],
        comment: values.comment || undefined,
      },
    });
    setShowAddDialog(false);
  }, [createMutation, planId, activeIndicators]);

  const handleCellUpdate = useCallback(async (
    rowId: string,
    field: string,
    newValue: ProgressEntryResponse[keyof ProgressEntryResponse],
  ) => {
    const entry = progressEntries.find((e) => e.id === rowId);
    if (!entry) return;

    const currentIndicators = (entry.indicators || []).map((ind: ProgressIndicatorDTO) => ({
      indicator_id: ind.indicator_id,
      value: ind.value,
    }));

    await updateMutation.mutateAsync({
      planId,
      entryId: rowId,
      data: {
        date: toOrgDateOnly(entry.date),
        employee_id: entry.employee_id,
        indicators: currentIndicators as ProgressIndicatorDTO[],
        comment: entry.comment,
        [field]: field === "date" ? toOrgDateOnly(newValue as string) : newValue,
        version: entry.version ?? 0,
      },
    });
  }, [updateMutation, planId, progressEntries]);

  // Static columns
  const staticColumns = useMemo<EditableCellConfig<ProgressEntryResponse>[]>(
    () => [
      { accessorKey: "date", header: t({ id: "settlingIn.progress.columns.date", message: "Date" }), inputType: "date", editable: true, sortable: true },
      {
        accessorKey: "employee_id",
        header: t({ id: "settlingIn.progress.columns.employee", message: "Employee" }),
        editable: false,
        // Every row renders this same combobox, so without row context they all
        // announce the identical "Responsible employee" — a screen-reader user
        // tabbing the column cannot tell which day they are editing. The date is
        // the row's identity here.
        cell: ({ row }: { row: { original: ProgressEntryResponse } }) => {
          // Bound to a local identifier on purpose: the `t` macro takes no
          // `values`, and it only extracts a NAMED `{date}` placeholder from a
          // bare identifier. Inlining `${toOrgDateOnly(...)}` would extract as
          // positional `{0}` and never bind — a permanently empty date.
          const date = toOrgDateOnly(row.original.date);
          return (
            <EmployeeSearchSelect
              value={row.original.employee_id}
              onChange={(id) => row.original.id && handleCellUpdate(row.original.id, "employee_id", id)}
              ariaLabel={t({
                id: "settlingIn.progress.employeeForDate",
                message: `Responsible employee for ${date}`,
              })}
            />
          );
        },
      },
    ],
    [t, handleCellUpdate],
  );

  // Dynamic indicator columns with custom TriStateButton cells
  const indicatorColumns = useMemo<EditableCellConfig<ProgressEntryResponse>[]>(
    () => activeIndicators.map((ind: IndicatorDefinitionDTO) => ({
      id: `indicator_${ind.id}`,
      accessorKey: "indicators" as keyof ProgressEntryResponse,
      header: getIndicatorLabel(ind),
      editable: false,
      sortable: false,
      size: 70,
      cell: ({ row }: { row: { original: ProgressEntryResponse } }) => (
        <div className="flex justify-center">
          <TriStateButton
            value={getEntryIndicatorValue(row.original, ind.id)}
            onChange={(val) => handleToggleIndicator(row.original, ind.id, val)}
            label={getIndicatorLabel(ind)}
          />
        </div>
      ),
    })),
    [activeIndicators, getIndicatorLabel, getEntryIndicatorValue, handleToggleIndicator],
  );

  // Comment + delete columns
  const trailingColumns = useMemo<EditableCellConfig<ProgressEntryResponse>[]>(
    () => [
      { accessorKey: "comment", header: t({ id: "settlingIn.progress.columns.comment", message: "Comment" }), inputType: "text", editable: true },
      {
        accessorKey: "id" as keyof ProgressEntryResponse,
        header: "",
        editable: false,
        cell: ({ row }: { row: { original: ProgressEntryResponse } }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => row.original.id && setDeleteEntryId(row.original.id)}
            disabled={deleteMutation.isPending}
            aria-label={t({ id: "common.delete", message: "Delete" })}
          >
            <Trash2 className="h-4 w-4 text-destructive" aria-hidden="true" />
          </Button>
        ),
      },
    ],
    [t, deleteMutation.isPending],
  );

  const allColumns = useMemo<EditableCellConfig<ProgressEntryResponse>[]>(
    () => [...staticColumns, ...indicatorColumns, ...trailingColumns],
    [staticColumns, indicatorColumns, trailingColumns],
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => {
          // Bump the nonce so the form remounts and reseeds `defaultValues`;
          // the shared form seeds at mount only.
          setAddDialogNonce((n) => n + 1);
          setShowAddDialog(true);
        }}>
          <Plus className="h-4 w-4 mr-1" />
          <Trans id="settlingIn.progress.addEntry">Add Progress</Trans>
        </Button>
      </div>

      {/* Trend strip ABOVE the entry table (ST-6/S19). It consumes the progress
          entries the detail endpoint already returns — no new endpoint — and the
          SAME `activeIndicators` array the table uses, so deactivated and
          reordered indicators are honoured identically. */}
      <ProgressTrendMatrix
        entries={sortedEntries}
        indicators={activeIndicators}
        getIndicatorLabel={getIndicatorLabel}
      />

      {sortedEntries.length === 0 ? (
        <div className="flex items-center justify-center h-32">
          <p className="text-muted-foreground">
            <Trans id="settlingIn.progress.emptyState">No progress entries yet. Click "Add Progress" to record behavioral assessments.</Trans>
          </p>
        </div>
      ) : (
        <BaseEditableTable<ProgressEntryResponse>
          data={sortedEntries}
          columns={allColumns}
          onCellUpdate={handleCellUpdate}
          tableId="settling-in-progress"
          defaultSortField="date"
          defaultSortOrder="desc"
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteEntryId}
        onOpenChange={(open) => !open && setDeleteEntryId(null)}
        title={<Trans id="settlingIn.progress.deleteConfirmTitle">Delete Progress Entry</Trans>}
        description={
          <Trans id="settlingIn.progress.deleteConfirmDescription">Are you sure you want to delete this progress entry? This action cannot be undone.</Trans>
        }
        confirmLabel={<Trans id="common.delete">Delete</Trans>}
        variant="destructive"
        onConfirm={() => (deleteEntryId ? handleDelete(deleteEntryId) : undefined)}
      />

      {/* Add Progress Dialog — on BaseEditableForm (ST-5/S16), so Escape-to-cancel
          and the aria-invalid / aria-describedby wiring come from the shared form
          instead of being hand-rolled. */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              <Trans id="settlingIn.progress.addEntryTitle">Add Progress Entry</Trans>
            </DialogTitle>
            <DialogDescription className="sr-only">
              <Trans id="common.dialog.formDescription" message="Fill in the fields, then save." />
            </DialogDescription>
          </DialogHeader>

          <BaseEditableForm<ProgressAddFormData>
            key={`progress-add-${planId}-${addDialogNonce}`}
            schema={addSchema}
            fields={addFields}
            groups={addGroups}
            defaultValues={addDefaultValues}
            mode="create"
            onSubmit={handleAdd}
            onCancel={() => setShowAddDialog(false)}
            isSubmitting={createMutation.isPending}
            inSheet
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
